import { Invoice } from './invoice.model';
import { List, Map } from 'immutable';
import {
  IEntityTypePayload,
  IListFilterPayload,
  IPropertiesPayload,
} from '../entity-data/entity-data.actions';
import { EntityTypesEnum } from '../../app.enum';
import {
  IAddManualPayment,
  IMarkAsPaidData,
} from '../payments-data/payments-data.data';
import {
  IDocumentIdPayload,
  ISetEditingDocument,
} from '../document-data/document-data.actions';
import { Schedule } from '../document-data/sub-models/schedule.model';
import { PaymentTransaction } from '../document-data/sub-models/payment-transaction.model';
import { IDispatchId } from '../../utils/helpers/redux.helper';
import { IRendererOptions } from '../renderer/renderer.interface';

// common interfaces
export interface IInvoicePayload {
  invoice: Invoice;
}

export interface IInvoiceListPayload {
  invoices: List<Invoice>;
}

export interface IPaymentTransactionPayload {
  paymentTransaction: PaymentTransaction;
}

export interface ITransactionIdPayload {
  transactionId: string;
}

export interface IInvoiceError {
  error: Error;
  errorData: any[];
}

// action payload interfaces
export interface ISetSchedulesList extends IEntityTypePayload {
  scheduleList: List<Schedule>;
  deletedIds: List<string>;
}

export interface IUpdateSchedule
  extends IEntityTypePayload,
    IDocumentIdPayload,
    IPropertiesPayload {}

export interface IDeleteSchedule
  extends IEntityTypePayload,
    IDocumentIdPayload {}

export interface ICreateManualPayment
  extends IDispatchId,
    IEntityTypePayload,
    ISetEditingDocument {
  addManualPaymentData: IAddManualPayment;
}

export interface IDeleteManualPayment
  extends IEntityTypePayload,
    IPaymentTransactionPayload {}

export interface ICreateManualPaymentSuccess
  extends IEntityTypePayload,
    IInvoicePayload {
  sourceAction: string;
}

export interface ICreateManualPaymentError
  extends IEntityTypePayload,
    IInvoiceError {}

export interface IMarkAsPaid
  extends IEntityTypePayload,
    IDocumentIdPayload,
    ISetEditingDocument {
  markAsPaidData?: IMarkAsPaidData;
}

export interface IMarkAsPaidSuccessV2
  extends IEntityTypePayload,
    IInvoicePayload {
  entityType: EntityTypesEnum;
  invoice: Invoice;
  isSendReceipt: boolean;
  transactionId: string;
  renderingOptions: IRendererOptions;
  sourceAction: string;
}

export interface IRefundEPayment
  extends IEntityTypePayload,
    ITransactionIdPayload {}

export interface IUnmatchTransaction
  extends IEntityTypePayload,
    ITransactionIdPayload {}

export interface ITransactionsUpdateSuccess
  extends IEntityTypePayload,
    IInvoicePayload {
  sourceAction: string;
}

export interface IMarkAsPaidError extends IEntityTypePayload, IInvoiceError {}

export interface IMarkSelectedInvoicesAsPaid
  extends IEntityTypePayload,
    IListFilterPayload {}

export interface IBatchMarkInvoiceAsPaid
  extends IMarkSelectedInvoicesAsPaid,
    IInvoiceListPayload {}

export interface IBatchMarkInvoiceAsPaidNetworkResult
  extends IEntityTypePayload,
    IInvoiceListPayload {
  sourceAction: string;
}

// action enums
export enum ActionsEnum {
  CREATE_MANUAL_PAYMENT = 'CREATE_MANUAL_PAYMENT',
  CREATE_MANUAL_PAYMENT_SUCCESS = 'CREATE_MANUAL_PAYMENT_SUCCESS',
  CREATE_MANUAL_PAYMENT_ERROR = 'CREATE_MANUAL_PAYMENT_ERROR',
  DELETE_MANUAL_PAYMENT = 'DELETE_MANUAL_PAYMENT',
  FETCH_SCHEDULES = 'FETCH_SCHEDULES',
  FETCH_SCHEDULES_ERROR = 'FETCH_SCHEDULES_ERROR',
  UPDATE_SCHEDULE = 'UPDATE_SCHEDULE',
  UPDATE_SCHEDULE_ERROR = 'UPDATE_SCHEDULE_ERROR',
  DELETE_SCHEDULE = 'DELETE_SCHEDULE',
  DELETE_SCHEDULE_ERROR = 'DELETE_SCHEDULE_ERROR',
  SET_SCHEDULES_LIST = 'SET_SCHEDULES_LIST',
  MARK_AS_PAID = 'MARK_AS_PAID',
  MARK_AS_PAID_SUCCESS = 'MARK_AS_PAID_SUCCESS',
  MARK_AS_PAID_SUCCESS_V2 = 'MARK_AS_PAID_SUCCESS_V2',
  MARK_AS_PAID_ERROR = 'MARK_AS_PAID_ERROR',
  REFUND_E_PAYMENT = 'REFUND_E_PAYMENT',
  UNMATCH_TRANSACTION = 'UNMATCH_TRANSACTION',
  TRANSACTIONS_UPDATE_SUCCESS = 'TRANSACTIONS_UPDATE_SUCCESS',
  MARK_SELECTED_INVOICES_AS_PAID = 'MARK_SELECTED_INVOICES_AS_PAID',
  BATCH_MARK_INVOICE_AS_PAID_STORE = 'BATCH_MARK_INVOICE_AS_PAID_STORE',
  BATCH_MARK_INVOICE_AS_PAID_NETWORK = 'BATCH_MARK_INVOICE_AS_PAID_NETWORK',
  BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS = 'BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS',
  BATCH_MARK_INVOICE_AS_PAID_NETWORK_ERROR = 'BATCH_MARK_INVOICE_AS_PAID_NETWORK_ERROR',
}

// action definitions
export interface FetchSchedules {
  type: ActionsEnum.FETCH_SCHEDULES;
}

export interface FetchSchedulesError {
  type: ActionsEnum.FETCH_SCHEDULES_ERROR;
}

export interface UpdateSchedule {
  type: ActionsEnum.UPDATE_SCHEDULE;
  payload: IUpdateSchedule;
}

export interface UpdateScheduleError {
  type: ActionsEnum.UPDATE_SCHEDULE_ERROR;
}

export interface DeleteSchedule {
  type: ActionsEnum.DELETE_SCHEDULE;
  payload: IDeleteSchedule;
}

export interface DeleteScheduleError {
  type: ActionsEnum.DELETE_SCHEDULE_ERROR;
}

export interface SetSchedulesList {
  type: ActionsEnum.SET_SCHEDULES_LIST;
  payload: ISetSchedulesList;
}

export interface CreateManualPayment {
  type: ActionsEnum.CREATE_MANUAL_PAYMENT;
  payload: ICreateManualPayment;
}

export interface DeleteManualPayment {
  type: ActionsEnum.DELETE_MANUAL_PAYMENT;
  payload: IDeleteManualPayment;
}

export interface CreateManualPaymentSuccess {
  type: ActionsEnum.CREATE_MANUAL_PAYMENT_SUCCESS;
  payload: ICreateManualPaymentSuccess;
}

export interface CreateManualPaymentError {
  type: ActionsEnum.CREATE_MANUAL_PAYMENT_ERROR;
  payload: ICreateManualPaymentError;
}

export interface MarkAsPaid {
  type: ActionsEnum.MARK_AS_PAID;
  payload: IMarkAsPaid;
}

export interface MarkAsPaidSuccessV2 {
  type: ActionsEnum.MARK_AS_PAID_SUCCESS_V2;
  payload: IMarkAsPaidSuccessV2;
}

export interface MarkAsPaidError {
  type: ActionsEnum.MARK_AS_PAID_ERROR;
  payload: IMarkAsPaidError;
}

export interface RefundEPayment {
  type: ActionsEnum.REFUND_E_PAYMENT;
  payload: IRefundEPayment;
}

export interface UnmatchTransaction {
  type: ActionsEnum.UNMATCH_TRANSACTION;
  payload: IUnmatchTransaction;
}

export interface TransactionsUpdateSuccess {
  type: ActionsEnum.TRANSACTIONS_UPDATE_SUCCESS;
  payload: ITransactionsUpdateSuccess;
}

export interface MarkSelectedInvoicesAsPaid {
  type: ActionsEnum.MARK_SELECTED_INVOICES_AS_PAID;
  payload: IMarkSelectedInvoicesAsPaid;
}

export interface BatchMarkInvoiceAsPaidStore {
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_STORE;
  payload: IBatchMarkInvoiceAsPaid;
}

export interface BatchMarkInvoiceAsPaidNetwork {
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK;
  payload: IBatchMarkInvoiceAsPaid;
}

export interface BatchMarkInvoiceAsPaidNetworkSuccess {
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS;
  payload: IBatchMarkInvoiceAsPaidNetworkResult;
}

export interface BatchMarkInvoiceAsPaidNetworkError {
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_ERROR;
  payload: IBatchMarkInvoiceAsPaidNetworkResult;
}

// action creators
export const fetchSchedules = (): FetchSchedules => ({
  type: ActionsEnum.FETCH_SCHEDULES,
});

export const fetchSchedulesError = (): FetchSchedulesError => ({
  type: ActionsEnum.FETCH_SCHEDULES_ERROR,
});

export const updateSchedule = (
  entityType: EntityTypesEnum,
  documentId: string,
  properties: Map<string, any>
): UpdateSchedule => ({
  type: ActionsEnum.UPDATE_SCHEDULE,
  payload: {
    entityType,
    documentId,
    properties,
  },
});

export const updateScheduleError = (): UpdateScheduleError => ({
  type: ActionsEnum.UPDATE_SCHEDULE_ERROR,
});

export const deleteSchedule = (
  entityType: EntityTypesEnum,
  documentId: string
): DeleteSchedule => ({
  type: ActionsEnum.DELETE_SCHEDULE,
  payload: {
    entityType,
    documentId,
  },
});

export const deleteScheduleError = (): DeleteScheduleError => ({
  type: ActionsEnum.DELETE_SCHEDULE_ERROR,
});

export const setSchedulesList = (
  entityType: EntityTypesEnum,
  scheduleList: List<Schedule>,
  deletedIds: List<string>
): SetSchedulesList => ({
  type: ActionsEnum.SET_SCHEDULES_LIST,
  payload: {
    entityType,
    scheduleList,
    deletedIds,
  },
});

export const createManualPayment = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  addManualPaymentData: IAddManualPayment,
  setEditingDocument: boolean
): CreateManualPayment => ({
  type: ActionsEnum.CREATE_MANUAL_PAYMENT,
  payload: {
    dispatchId,
    entityType,
    addManualPaymentData,
    setEditingDocument,
  },
});

export const deleteManualPayment = (
  entityType: EntityTypesEnum,
  paymentTransaction: PaymentTransaction
): DeleteManualPayment => ({
  type: ActionsEnum.DELETE_MANUAL_PAYMENT,
  payload: {
    entityType,
    paymentTransaction,
  },
});

export const createManualPaymentSuccess = (
  entityType: EntityTypesEnum,
  invoice: Invoice
): CreateManualPaymentSuccess => ({
  type: ActionsEnum.CREATE_MANUAL_PAYMENT_SUCCESS,
  payload: {
    entityType,
    invoice,
    sourceAction: ActionsEnum.CREATE_MANUAL_PAYMENT,
  },
});

export const createManualPaymentError = (
  entityType: EntityTypesEnum,
  error: Error,
  errorData: any[]
): CreateManualPaymentError => ({
  type: ActionsEnum.CREATE_MANUAL_PAYMENT_ERROR,
  payload: {
    entityType,
    error,
    errorData,
  },
});

export const markAsPaid = (
  entityType: EntityTypesEnum,
  documentId: string,
  markAsPaidData?: IMarkAsPaidData,
  setEditingDocument?: boolean
): MarkAsPaid => ({
  type: ActionsEnum.MARK_AS_PAID,
  payload: {
    entityType,
    documentId,
    markAsPaidData,
    setEditingDocument,
  },
});

export const markAsPaidSuccessV2 = (
  entityType: EntityTypesEnum,
  invoice: Invoice,
  isSendReceipt: boolean,
  transactionId: string,
  renderingOptions: IRendererOptions
): MarkAsPaidSuccessV2 => ({
  type: ActionsEnum.MARK_AS_PAID_SUCCESS_V2,
  payload: {
    entityType,
    invoice,
    isSendReceipt,
    transactionId,
    renderingOptions,
    sourceAction: ActionsEnum.MARK_AS_PAID,
  },
});

export const markAsPaidError = (
  entityType: EntityTypesEnum,
  error: Error,
  errorData: any[]
): MarkAsPaidError => ({
  type: ActionsEnum.MARK_AS_PAID_ERROR,
  payload: {
    entityType,
    error,
    errorData,
  },
});

export const refundEPayment = (
  entityType: EntityTypesEnum,
  transactionId: string
): RefundEPayment => ({
  type: ActionsEnum.REFUND_E_PAYMENT,
  payload: {
    entityType,
    transactionId,
  },
});

export const unmatchTransaction = (
  entityType: EntityTypesEnum,
  transactionId: string
): UnmatchTransaction => ({
  type: ActionsEnum.UNMATCH_TRANSACTION,
  payload: {
    entityType,
    transactionId,
  },
});

export const transactionsUpdateSuccess = (
  entityType: EntityTypesEnum,
  invoice: Invoice,
  sourceAction: string
): TransactionsUpdateSuccess => ({
  type: ActionsEnum.TRANSACTIONS_UPDATE_SUCCESS,
  payload: {
    entityType,
    invoice,
    sourceAction,
  },
});

export const markSelectedInvoicesAsPaid = (
  entityType: EntityTypesEnum,
  listFilter?: string
): MarkSelectedInvoicesAsPaid => ({
  type: ActionsEnum.MARK_SELECTED_INVOICES_AS_PAID,
  payload: {
    entityType,
    listFilter,
  },
});

export const batchMarkInvoiceAsPaidStore = (
  entityType: EntityTypesEnum,
  invoices: List<Invoice>,
  listFilter?: string
): BatchMarkInvoiceAsPaidStore => ({
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_STORE,
  payload: {
    entityType,
    invoices,
    listFilter,
  },
});

export const batchMarkInvoiceAsPaidNetwork = (
  entityType: EntityTypesEnum,
  invoices: List<Invoice>,
  listFilter?: string
): BatchMarkInvoiceAsPaidNetwork => ({
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK,
  payload: {
    entityType,
    invoices,
    listFilter,
  },
});

export const batchMarkInvoiceAsPaidNetworkSuccess = (
  entityType: EntityTypesEnum,
  invoices: List<Invoice>
): BatchMarkInvoiceAsPaidNetworkSuccess => ({
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_SUCCESS,
  payload: {
    entityType,
    invoices,
    sourceAction: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK,
  },
});

export const batchMarkInvoiceAsPaidNetworkError = (
  entityType: EntityTypesEnum,
  invoices: List<Invoice>
): BatchMarkInvoiceAsPaidNetworkError => ({
  type: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK_ERROR,
  payload: {
    entityType,
    invoices,
    sourceAction: ActionsEnum.BATCH_MARK_INVOICE_AS_PAID_NETWORK,
  },
});
